import React, { useState, useEffect } from 'react';
import { entityAPI } from '../../../services/api';
import { Building2, Plus, Pencil, CheckCircle, XCircle, X, AlertCircle, Filter, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import Pagination from '../../../components/common/Pagination';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const Entities = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState({
    entity_name: '',
    entity_id: ''
  });
  
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchEntities();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, selectedEntityId]);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      const response = await entityAPI.getAll(1, 100);
      
      let entitiesData = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        entitiesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        entitiesData = response.data;
      }
      
      setEntities(entitiesData);
    } catch (error) {
      setError('Failed to fetch entities');
    } finally {
      setLoading(false);
    }
  };

  // PROMPT-099: Sanitize Entity ID - keep only digits (0-9)
  const sanitizeEntityId = (entityId) => {
    if (!entityId) return entityId;
    return entityId.replace(/[^0-9]/g, '');
  };

  // PROMPT-099: Restrict Entity ID input to digits only at typing level
  const handleEntityIdChange = (e) => {
    const sanitized = sanitizeEntityId(e.target.value);
    setFormData({ ...formData, entity_id: sanitized });
  };

  const handleEntityIdPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const sanitized = sanitizeEntityId(pastedText);
    setFormData({ ...formData, entity_id: sanitized });
  };

  const handleEntityIdBlur = (e) => {
    const sanitizedId = sanitizeEntityId(e.target.value);
    setFormData({ ...formData, entity_id: sanitizedId });
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // PROMPT-099: Sanitize Entity ID before sending to backend
    const sanitizedEntityId = sanitizeEntityId(formData.entity_id);

    try {
      if (editingEntity) {
        await entityAPI.update(editingEntity.id, { 
          entity_name: formData.entity_name,
          entity_id: sanitizedEntityId 
        });
        setSuccess('Entity updated successfully');
      } else {
        await entityAPI.create({
          entity_name: formData.entity_name,
          entity_id: sanitizedEntityId
        });
        setSuccess('Entity created successfully');
      }
      fetchEntities();
      handleCloseModal();
    } catch (error) {
      setError(error.response?.data?.error || 'Operation failed');
    }
  };

  const handleEdit = (entity) => {
    setEditingEntity(entity);
    setFormData({
      entity_name: entity.entity_name,
      entity_id: entity.entity_id
    });
    setShowModal(true);
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Are you sure you want to disable this entity?')) return;
    
    try {
      await entityAPI.disable(id);
      setSuccess('Entity disabled successfully');
      fetchEntities();
    } catch (error) {
      setError('Failed to disable entity');
    }
  };

  const handleEnable = async (id) => {
    try {
      await entityAPI.enable(id);
      setSuccess('Entity enabled successfully');
      fetchEntities();
    } catch (error) {
      setError('Failed to enable entity');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEntity(null);
    setFormData({
      entity_name: '',
      entity_id: ''
    });
  };

  // Generate dropdown options
  const entityOptions = [
    { value: '', label: 'All Entities' },
    ...entities.map(e => ({
      value: e.id.toString(),
      label: `${e.entity_name} (${e.entity_id})`
    }))
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  // Filter entities
  const filteredEntities = entities.filter(entity => {
    const matchesEntity = !selectedEntityId || entity.id.toString() === selectedEntityId;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && entity.is_active) ||
      (filterStatus === 'inactive' && !entity.is_active);

    return matchesEntity && matchesStatus;
  });

  // Client-side pagination
  const clientTotalPages = Math.ceil(filteredEntities.length / pageSize) || 1;
  const paginatedEntities = filteredEntities.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const activeFilterCount = [selectedEntityId, filterStatus].filter(v => v && v.trim() !== '').length;

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Building2 className="w-5 h-5 text-white" />
            </div>
            Entity Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage DLT entities and their configurations</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchEntities}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all hover:shadow-sm"
            style={{ background: 'rgba(99, 102, 241, 0.06)', border: '1px solid rgba(99, 102, 241, 0.12)', color: '#4f46e5' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
          >
            <Plus className="w-3.5 h-3.5" />
            Add Entity
          </button>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-800 flex-1">{error}</p>
          <button onClick={() => setError('')}><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}
      {success && (
        <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-green-800 flex-1">{success}</p>
          <button onClick={() => setSuccess('')}><X className="w-4 h-4 text-green-400" /></button>
        </div>
      )}

      {/* Collapsible Filter Panel */}
      <div className="bg-white rounded-xl" style={cardStyle}>
        <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50/50 transition-colors cursor-pointer select-none"
          style={{ background: 'rgba(99, 102, 241, 0.02)' }}
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4" style={{ color: '#6366f1' }} />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white rounded-full" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                {activeFilterCount}
              </span>
            )}
          </div>
          {filtersOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>

        {filtersOpen && (
          <div style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-3 p-4">
              <div className="min-w-0 sm:col-span-2">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity</label>
                <SearchableDropdown
                  options={entityOptions}
                  value={selectedEntityId}
                  onChange={setSelectedEntityId}
                  placeholder="Search entities..."
                  clearable={true}
                />
              </div>
              <div className="min-w-0">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
                <SearchableDropdown
                  options={statusOptions}
                  value={filterStatus}
                  onChange={setFilterStatus}
                  placeholder="All Status"
                  clearable={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
        {/* Table Header Bar */}
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
          <span className="text-xs font-medium text-gray-500">
            Showing {paginatedEntities.length} of {filteredEntities.length} entities
          </span>
        </div>

        {loading ? (
          <div className="p-16 flex flex-col items-center">
            <RefreshCw className="w-8 h-8 animate-spin mb-3" style={{ color: '#6366f1' }} />
            <p className="text-sm text-gray-500">Loading entities...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider w-12">No</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Entity Name</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Entity ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Sender ID</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Mapped Users</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEntities.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <Building2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#c7d2fe' }} />
                      <p className="text-sm text-gray-400 font-medium">No entities found</p>
                    </td>
                  </tr>
                ) : (
                  paginatedEntities.map((entity, index) => (
                    <tr key={entity.id} className="transition-colors hover:bg-indigo-50/30" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.05)' }}>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {(currentPage - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-semibold text-gray-800">{entity.entity_name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-gray-600">{entity.entity_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600">{entity.sender_id || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {entity.mapped_users && entity.mapped_users.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {entity.mapped_users.map((user) => (
                              <span
                                key={user.id}
                                className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-full"
                                style={{ background: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' }}
                                title={user.full_name || user.username}
                              >
                                {user.username}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 italic">No users</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {entity.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(entity)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-indigo-50"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" style={{ color: '#6366f1' }} />
                          </button>
                          {entity.is_active ? (
                            <button
                              onClick={() => handleDisable(entity.id)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-red-50"
                              title="Disable"
                            >
                              <XCircle className="w-3.5 h-3.5 text-red-500" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleEnable(entity.id)}
                              className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-green-50"
                              title="Enable"
                            >
                              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            
            {/* Pagination Footer */}
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecords={filteredEntities.length}
              totalPages={clientTotalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" style={{ background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl" style={{ border: '1px solid rgba(99, 102, 241, 0.1)' }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.08)' }}>
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
                  {editingEntity ? <Pencil className="w-3.5 h-3.5 text-white" /> : <Plus className="w-3.5 h-3.5 text-white" />}
                </div>
                {editingEntity ? 'Edit Entity' : 'Create New Entity'}
              </h3>
              <button onClick={handleCloseModal} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity Name *</label>
                <input
                  type="text"
                  required
                  value={formData.entity_name}
                  onChange={(e) => setFormData({ ...formData, entity_name: e.target.value })}
                  className="w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                  placeholder="Enter entity name"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Entity ID (DLT) *</label>
                <input
                  type="text"
                  required
                  value={formData.entity_id}
                  onChange={handleEntityIdChange}
                  onPaste={handleEntityIdPaste}
                  onBlur={handleEntityIdBlur}
                  className="w-full h-9 px-3 text-sm rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
                  placeholder="Enter DLT Entity ID (digits only)"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 rounded-xl text-xs font-medium transition-all"
                  style={{ color: '#6b7280', background: 'rgba(107, 114, 128, 0.06)', border: '1px solid rgba(107, 114, 128, 0.15)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md"
                  style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}
                >
                  {editingEntity ? 'Update Entity' : 'Create Entity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entities;
